import React, { useRef } from 'react';
import useFitText from 'use-fit-text';
import { Cell } from '../types';
import { getDurationInHoursAndMinutesBetween } from '@teetsh/app/src/utils/time';
import { EDTPDFSettings } from '@teetsh/app/src/types/edt';
import {
  bgColors,
  getTextColorFromBackgroundColor,
} from '@teetsh/app/src/components/colors';
import tw from 'twin.macro';
import { Color } from '@teetsh/app/src/types/colors';
import { Classroom } from '@teetsh/app/src/types/classroom';
import { ClassroomsOrg } from '@teetsh/app/src/types/school';
import getClassroomsAvailableForThatDay from '@teetsh/app/src/components/EDT/getClassroomsAvailableForThatDay';
import getDay from '@teetsh/app/src/utils/time/getDay';
import { getClassroomToDisplayAsString } from '@teetsh/app/src/containers/CahierJournal/DayColumn/Seance/getClassroomsToDisplayAsString';
import { TagColorWithIntensity } from '@teetsh/app/src/styles/userConstants';

interface Props {
  cell: Cell;
  settings: EDTPDFSettings;
  classrooms: Classroom[];
  classroomsOrg: ClassroomsOrg;
}

function getCellTextColor(
  settings: EDTPDFSettings,
  cellColor?: Color | TagColorWithIntensity,
) {
  if (!cellColor || settings.eventColor === 'black') {
    return tw`text-black!`;
  }
  if (settings.eventColor === 'white') {
    return tw`text-white!`;
  }
  return getTextColorFromBackgroundColor(cellColor, true);
}

function getCellTitle(
  duration: string,
  cellTitle: string,
  classrooms?: string,
) {
  let title = cellTitle;
  if (classrooms && classrooms !== '') {
    title = `${title} ${classrooms}`;
  }
  if (duration !== '') {
    title = `${title} ${duration}`;
  }
  return title;
}

export default function EventCell({
  cell,
  settings,
  classrooms,
  classroomsOrg,
}: Props) {
  const tdRef = useRef(null);
  const { event } = cell;
  const { fontSize, ref } = useFitText({
    minFontSize: 40,
    resolution: 1,
  });
  const duration =
    event && settings.displaylength
      ? `(${getDurationInHoursAndMinutesBetween(event.start, event.end)})`
      : '';

  if (
    !cell.title ||
    cell.title === '' ||
    cell.title === ' ' ||
    cell.title === '.'
  ) {
    return (
      <td
        css={[
          cell.color && bgColors(cell.color),
          cell.height && `height: ${cell.height}px`,
          cell.isVisuallyBottomRight && tw`rounded-br-lg`,
        ]}
        rowSpan={cell.rowspan}
        colSpan={cell.colspan}
      />
    );
  }

  const classroomsForThatDay = event?.start
    ? getClassroomsAvailableForThatDay(classrooms, getDay(event.start))
    : classrooms;

  const classroomsToDisplay = getClassroomToDisplayAsString(
    event?.classroomIds || [],
    classroomsForThatDay,
    classroomsOrg,
  );
  const title = getCellTitle(duration, cell.title, classroomsToDisplay);
  return (
    <td
      css={[
        cell.color && bgColors(cell.color),
        cell.isVisuallyBottomRight && tw`rounded-br-lg`,
      ]}
      rowSpan={cell.rowspan}
      ref={tdRef}
      colSpan={cell.colspan}
    >
      <div
        ref={ref}
        css={[
          tw`text-center flex justify-center items-center overflow-hidden px-1`,
          getCellTextColor(settings, cell.color),
        ]}
        className="cell-bitch"
        style={{
          height: cell.height,
          fontSize,
        }}
      >
        {title}
      </div>
    </td>
  );
}
